import { Link, useLocation } from "react-router-dom"
import { useState, useRef, useEffect } from "react"
import {
  FiHome,
  FiCalendar,
  FiShoppingCart,
  FiSettings,
  FiChevronDown,
  FiChevronUp,
  FiUser,
  FiLogOut,
  FiX,
  FiUsers,
  FiPackage,
  FiLayers,
  FiFileText,
  FiPlus,
  FiList,
  FiEdit3,
  FiMenu
} from "react-icons/fi"
import { useAuthStore } from "../../../store/authStore"
import { useNavigate } from "react-router-dom"
import { BiHome, BiLogOut, BiUser } from "react-icons/bi"
import { COLORS } from "@/utils/colors"
import { useCartStore } from "@/store/cartStore"
import { HiOutlineShoppingBag } from "react-icons/hi2"

interface NavbarProps {
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  forceHamburger?: boolean
}

interface NavItem {
  path: string
  label: string
  icon: React.ReactNode
  roles: string[]
  subItems?: NavItem[]
}

const Navbar: React.FC<NavbarProps> = ({ sidebarOpen, setSidebarOpen, forceHamburger }) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const [showCustomQuote, setShowCustomQuote] = useState(false)
  const [showEnclosureBuilder, setShowEnclosureBuilder] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const profileDropdownRef = useRef<HTMLDivElement>(null)
  const profileRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  // Navigation items with role-based access
  const navItems: NavItem[] = [
    {
      path: "/dashboard/view-templates",
      label: "Templates",
      icon: <FiHome className="w-5 h-5" />,
      roles: ["user"]
    },
    {
      path: user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'sub-admin' ? "/dashboard/admin-home" : "/dashboard/home",
      label: "Home",
      icon: <FiHome className="w-5 h-5" />,
      roles: ["user", "admin", "sub-admin"]
    },
    {
      path: "/dashboard/orders",
      label: "Orders",
      icon: <FiCalendar className="w-5 h-5" />,
      roles: ["user"]
    },
    {
      path: "/dashboard/saved-carts",
      label: "Carts",
      icon: <FiShoppingCart className="w-5 h-5" />,
      roles: ["user"]
    },
    {
      path: "/dashboard/enclosure-builder",
      label: "Enclosure",
      icon: <FiEdit3 className="w-5 h-5" />,
      roles: ["user"],
      subItems: [
        {
          path: "/dashboard/enclosure-builder/create",
          label: "Enclosure Builders",
          icon: <FiPlus className="w-4 h-4" />,
          roles: ["user"]
        },
        {
          path: "/dashboard/enclosure-builder/designs",
          label: "View Enclosures",
          icon: <FiList className="w-4 h-4" />,
          roles: ["user"]
        }
      ]
    },
    {
      path: "/dashboard/custom-quote",
      label: "Quote",
      icon: <FiFileText className="w-5 h-5" />,
      roles: ["user"],
      subItems: [
        {
          path: "/dashboard/quote-form",
          label: "Add New Request",
          icon: <FiPlus className="w-4 h-4" />,
          roles: ["user"]
        },
        {
          path: "/dashboard/custom-quote/requests",
          label: "View Requests",
          icon: <FiList className="w-4 h-4" />,
          roles: ["user"]
        }
      ]
    },

    // Admin-only items
    {
      path: "/dashboard/users",
      label: "Customers",
      icon: <FiUsers className="w-5 h-5" />,
      roles: ["admin", "sub-admin"]
    },
    {
      path: "/dashboard/products",
      label: "Products",
      icon: <FiPackage className="w-5 h-5" />,
      roles: ["admin", "sub-admin"]
    },
    {
      path: "/dashboard/admin/orders",
      label: "Orders",
      icon: <FiLayers className="w-5 h-5" />,
      roles: ["admin", "sub-admin"]
    },
    {
      path: "/dashboard/enclosure-management",
      label: "Enclosures",
      icon: <FiLayers className="w-5 h-5" />,
      roles: ["admin", "sub-admin"]
    },
    {
      path: "/dashboard/custom-quote-management",
      label: "Quotes",
      icon: <FiLayers className="w-5 h-5" />,
      roles: ["admin", "sub-admin"]
    },
    {
      path: "/dashboard/settings",
      label: "Settings",
      icon: <FiSettings className="w-5 h-5" />,
      roles: ["admin", "sub-admin"]
    },
  ]

  // Close dropdown when route changes
  useEffect(() => {
    setShowDropdown(false);
    setShowCustomQuote(false);
    setShowEnclosureBuilder(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      // Check if click is outside profile dropdown
      if (profileDropdownRef.current && profileRef.current &&
        !profileDropdownRef.current.contains(target) &&
        !profileRef.current.contains(target)) {
        setShowDropdown(false);
      }

      // Check if click is outside navigation dropdowns
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowCustomQuote(false);
        setShowEnclosureBuilder(false);
      }

      // Check if click is outside mobile menu
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setMobileMenuOpen(false);
        setShowCustomQuote(false);
        setShowEnclosureBuilder(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Disable scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Disable scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Re-enable scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function to ensure scroll is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout()
    navigate("/auth/login")
  }

  // const isActive = (path: string) => {
  //   if (path === "/dashboard") {
  //     // Home: highlight only on exact match or if no other nav matches
  //     return location.pathname === "/dashboard" ||
  //       !filteredNavItems.some(item => item.path !== "/dashboard" && location.pathname.startsWith(item.path));
  //   }
  //   return location.pathname === path || location.pathname.startsWith(path + '/');
  // }

  const isActive = (path: string, subItems?: NavItem[]) => {
    if (location.pathname === path || location.pathname.startsWith(path + '/')) {
      return true
    }

    if (subItems && subItems.length > 0) {
      return subItems.some((sub) =>
        location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')
      )
    }

    return false
  }


  // Filter navigation items based on user role
  const filteredNavItems = navItems.filter(item =>
    item.roles.includes(user?.role?.toLowerCase() || 'user')
  )

  const [isCartLoading, setIsCartLoading] = useState(false);


  const { isAuthenticated } = useAuthStore();
  const { items, getTotalItems, getTotalPrice, fetchCart } = useCartStore();

  const cartTotalPrice = getTotalPrice().toFixed(2);

  // Function to handle cart click
  const handleCartClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default navigation

    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate("/auth/login");
      return;
    }

    try {
      setIsCartLoading(true);
      // Fetch latest cart data
      await fetchCart();
      // Then navigate to cart page
      navigate("/cart");
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setIsCartLoading(false);
    }
  };

  // console.log("navItems",navItems)

  return (
    <div
      style={{ borderBottom: `2px solid ${COLORS.primary}` }}
      className={`fixed top-0 left-0 right-0 z-40 w-full bg-[#111] flex flex-row items-center h-[100px] px-4  text-white transition-transform duration-300`}
    >
      {/* Logo */}
      <div onClick={() => navigate("/")} className="flex items-center mr-4 pt-0 px-5 cursor-pointer">
        <img
          src="/images/home/Obscura_White_Logo2.png"
          alt="Obscura Logo"
          className="h-[85px]"
        />
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 flex items-center">
        {/* Desktop Nav (large screens, no icons) */}
        <div className="hidden lg:flex flex-row items-center h-full whitespace-nowrap scrollbar-hide">
          <ul className="flex  flex-row items-center space-x-2 h-full">
            {filteredNavItems.map((item) => (
              <li key={item.path} className="h-full flex items-center">
                {item.subItems ? (
                  <div className="h-full   relative flex items-center">
                    <button
                      // style={{ backgroundColor: isActive(item.path) ? COLORS.orange : "transparent" }}
                      style={{ backgroundColor: isActive(item.path, item.subItems) ? COLORS.orange : "transparent" }}

                      className={`flex items-center gap-2 px-3 py-2 rounded-md h-full  `}
                      onClick={() => {
                        if (item.label === "Quote") {
                          setShowCustomQuote(!showCustomQuote);
                          setShowEnclosureBuilder(false);
                        }
                        if (item.label === "Enclosure") {
                          setShowEnclosureBuilder(!showEnclosureBuilder);
                          setShowCustomQuote(false);
                        }
                      }}
                    >
                      {/* Hide icon on lg and up */}
                      {/* <span className="lg:hidden">{item.icon}</span> */}
                      <span>{item.label}</span>
                      {(item.label === "Quote" || item.label === "Enclosure") && (
                        (item.label === "Quote" && showCustomQuote) ||
                          (item.label === "Enclosure" && showEnclosureBuilder) ? (
                          <FiChevronUp className="w-4 h-4 ml-1" />
                        ) : (
                          <FiChevronDown className="w-4 h-4 ml-1" />
                        )
                      )}
                    </button>
                    {/* //// */}
                    {(item.label === "Quote" && showCustomQuote) ||
                      (item.label === "Enclosure" && showEnclosureBuilder) ? (
                      <div ref={dropdownRef} className="absolute top-full left-0 min-w-[200px] bg-[#1a1a1a] rounded-lg shadow-lg overflow-hidden z-10 mt-2">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            style={{ backgroundColor: isActive(subItem.path) ? COLORS.orange : "transparent" }}
                            className={`flex items-center gap-2 px-3 py-2 text-sm  `}
                            onClick={() => item.label === "Quote" ? setShowCustomQuote(false) : setShowEnclosureBuilder(false)}
                          >
                            {/* Hide icon on lg and up */}
                            {/* <span className="lg:hidden">{subItem.icon }</span> */}
                            <span>{subItem.label}</span>
                          </Link>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    style={{ backgroundColor: isActive(item.path) ? COLORS.orange : "transparent" }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md h-full `}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {/* Hide icon on lg and up */}
                    {/* <span className="lg:hidden">{item.icon}</span> */}
                    <span>{item.label}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
        {/* Mobile/Medium Nav (show icons) */}
        {mobileMenuOpen && (
          <div ref={mobileMenuRef} className="pointer-events-auto z-[9999]">
            <ul
              onMouseDown={(e) => e.stopPropagation()}
              className=" absolute top-0 h-screen w-[300px] right-0 bg-[#111] flex flex-col lg:hidden shadow-lg border-gray-800 animate-fade-in z-50">
              <>
                {/* Logo */}
                <div onClick={() => navigate("/")} className="flex items-center cursor-pointer justify-center h-[64px]">
                  <img
                    src="/images/home/Obscura_White_Logo2.png"
                    alt="Obscura Logo"
                    className="h-[80%] w-[15%]"
                  />
                </div>
              </>
              {filteredNavItems.map((item) => (
                <li key={item.path} className="relative">
                  {item.subItems ? (
                    <div>
                      <button
                        style={{ backgroundColor: isActive(item.path, item.subItems) ? COLORS.orange : "transparent" }}
                        className={`flex items-center gap-2 px-4 py-3 w-full text-left `}
                        onClick={() => {
                          if (item.label === "Quote") {
                            setShowCustomQuote(!showCustomQuote);
                            setShowEnclosureBuilder(false);
                          }
                          if (item.label === "Enclosure") {
                            setShowEnclosureBuilder(!showEnclosureBuilder);
                            setShowCustomQuote(false);
                          }
                        }}
                      >
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                        {(item.label === "Quote" || item.label === "Enclosure") && (
                          (item.label === "Quote" && showCustomQuote) ||
                            (item.label === "Enclosure" && showEnclosureBuilder) ? (
                            <FiChevronUp className="w-4 h-4 ml-1" />
                          ) : (
                            <FiChevronDown className="w-4 h-4 ml-1" />
                          )
                        )}
                      </button>
                      {/* Dropdown for subitems, collapsible */}
                      {((item.label === "Quote" && showCustomQuote) || (item.label === "Enclosure" && showEnclosureBuilder)) && (
                        <div className="pl-8 bg-[#181818]">
                          {item.subItems.map((subItem) => (
                            <div
                              key={subItem.path}
                              style={{ backgroundColor: isActive(subItem.path) ? COLORS.orange : "transparent" }}
                              className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer`}
                              onClick={() => {
                                navigate(subItem.path);
                                setTimeout(() => {
                                  setMobileMenuOpen(false);
                                  setShowCustomQuote(false);
                                  setShowEnclosureBuilder(false);
                                }, 50);
                              }}
                            >
                              <span>{subItem.icon}</span>
                              <span>{subItem.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{ backgroundColor: isActive(item.path) ? COLORS.orange : "transparent" }}
                      className={`flex items-center gap-2 px-4 py-3 w-full cursor-pointer`}
                      onClick={() => {
                        navigate(item.path);
                        setTimeout(() => setMobileMenuOpen(false), 50);
                      }}
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  )}
                </li>
              ))}
              {/* Go to Site in mobile menu with icon */}



              <li>
                <Link
                  to="/"
                  className="flex items-center gap-2 px-4 py-3 w-full hover:bg-gray-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileMenuOpen(false);
                  }}
                >
                  <BiHome className="w-5 h-5" />
                  <span className="text-sm">Go to Site</span>
                </Link>
              </li>

              {/* User Profile Section in mobile menu */}
              <li className="border-t border-gray-800 mt-4 pt-4">
                <div className="px-2 space-y-1">
                  <Link
                    to="/dashboard/profile"
                    className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-gray-800 rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <BiUser className="w-5 h-5" />
                    <span>My account</span>
                  </Link>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center gap-2 px-2 py-2 text-sm hover:bg-gray-800 rounded w-full text-left"
                  >
                    <BiLogOut className="w-5 h-5" />
                    <span>Sign out</span>
                  </button>
                </div>
              </li>
            </ul>
          </div>
        )}
      </nav>

      {/* Go to Site */}
      <div className="flex items-center h-full ml-2">

        {isAuthenticated && user?.role?.toLowerCase() === "user" && location.pathname.startsWith("/dashboard") && (
          <button onClick={handleCartClick} className="flex items-center gap-2 group relative ">
            <div className="relative ">
              {isCartLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              ) : (
                <HiOutlineShoppingBag className="text-xl xl:text-2xl" />
              )}
              {getTotalItems() > 0 && (
                <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold">
                  {getTotalItems()}
                </span>
              )}
            </div>
          </button>
        )}

        {/* <Link
          to="/"
          style={{ backgroundColor: isActive('/dashboard/') ? COLORS.orange : "transparent" }}
          className={`hidden lg:flex items-center gap-2 px-3 ml-4 py-2 rounded-md h-full border-l border-gray-800`}
          onClick={() => setSidebarOpen(false)}
        > 
          <span className="text-sm">Go to Site</span>
        </Link> */}
      </div>

      {/* User Profile with Dropdown and Hamburger on right  */}
      <div
        ref={profileRef}
        className="hidden lg:flex items-center gap-3 px-4 ml-4 py-2 border-l border-gray-800 cursor-pointer h-full"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <div className="flex flex-col items-start justify-center">
          <div className="text-sm font-medium">{user?.username || 'User'}</div>
          <div className="text-xs text-gray-400">{user?.email || 'user@example.com'}</div>
          <div className="text-xs text-gray-400 capitalize">{user?.role || 'user'}</div>
        </div>
        {showDropdown ? (
          <FiChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <FiChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </div>

      {/* Hamburger for mobile and medium screens only, now on right */}
      <div className="flex z-10 items-center lg:hidden ml-2">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-md hover:bg-gray-800 focus:outline-none">
          {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
        </button>
      </div>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div
          ref={profileDropdownRef}
          className="absolute hidden lg:flex flex-col top-full right-0 w-[200px] bg-[#1a1a1a] rounded-lg shadow-lg overflow-hidden z-10 mt-2"
        >
          <div className="py-1">
            <Link
              to="/dashboard/profile"
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800"
              onClick={() => setShowDropdown(false)}
            >
              <FiUser className="w-4 h-4 text-gray-400" />
              <span className="text-sm">My account</span>
            </Link>

            <div className="border-t border-gray-800 my-1"></div>
            <button
              onClick={() => {
                setShowDropdown(false);
                handleLogout();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800"
            >
              <FiLogOut className="w-4 h-4 text-gray-400" />
              <span className="text-sm">Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Navbar 
